/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import type {RowV5ResponseBody} from '../types/api/data';
import {createLruMap} from '../utils/createLruMap';

/**
 * Module-level cache for page row data.
 * Survives component re-renders so previously fetched pages are returned instantly
 * when navigating back (e.g. page 1 → page 2 → page 1).
 * Keyed by `gridCode:queryId:offset:limit:columns`. Capped at 50 entries — oldest evicted first.
 * Entries expire after {@link PAGE_DATA_CACHE_TTL_MS} milliseconds to bound staleness
 * when data is mutated outside the grid.
 */
const PAGE_DATA_CACHE_MAX_SIZE = 50;
/**
 * Entries older than this are treated as misses — bounds how stale an
 * externally-mutated page can appear when paginating back.
 */
const PAGE_DATA_CACHE_TTL_MS = 60_000;

type PageCacheEntry = {data: RowV5ResponseBody[]; timestamp: number};

const pageDataCache = createLruMap<string, PageCacheEntry>(PAGE_DATA_CACHE_MAX_SIZE);

/** @internal Clear the module-level cache. Used only by test suites. */
export function _clearPageDataCache(): void {
  pageDataCache.clear();
}

/** Removes all cached entries whose key starts with `gridCode:`. */
export function clearPageDataCacheForGrid(gridCode: string): void {
  const prefix = `${gridCode}:`;
  for (const key of pageDataCache.keys()) {
    if (key.startsWith(prefix)) {
      pageDataCache.delete(key);
    }
  }
}

/**
 * Builds a composite cache key from grid, query, pagination, and column parameters.
 * Format: `gridCode:queryId:offset:limit:col1,col2,...`
 */
export function getPageDataCacheKey(
  gridCode: string,
  queryId: string,
  offset: number,
  limit: number,
  columns: string[]
): string {
  return `${gridCode}:${queryId}:${offset}:${limit}:${columns.join(',')}`;
}

/** Returns cached row data for the given page key, or `undefined` on a cache miss or TTL expiry. */
export function getCachedPageData(key: string): RowV5ResponseBody[] | undefined {
  const entry = pageDataCache.get(key);
  if (!entry) {
    return undefined;
  }
  if (Date.now() - entry.timestamp > PAGE_DATA_CACHE_TTL_MS) {
    pageDataCache.delete(key);
    return undefined;
  }
  return entry.data;
}

/**
 * Stores row data in the page cache with LRU eviction.
 * If the cache exceeds {@link PAGE_DATA_CACHE_MAX_SIZE} entries, the oldest entry is evicted.
 */
export function setCachedPageData(key: string, data: RowV5ResponseBody[]): void {
  pageDataCache.set(key, {data, timestamp: Date.now()});
}
